
import { supabase } from "@/integrations/supabase/client";
import { v4 as uuidv4 } from "uuid";
import { MembershipData } from "../types/membership";

// Sauvegarder une nouvelle demande d'adhésion
export async function saveMembership(membershipData: MembershipData) {
  try {
    console.log("Sauvegarde d'une nouvelle adhésion:", membershipData);
    
    // Calcul du montant total
    let baseAmount = 0;
    
    if (membershipData.membershipType === "active") {
      baseAmount = 50;
    } else if (membershipData.membershipType === "benefactor") {
      baseAmount = 200;
      // Ajouter le droit d'entrée pour les membres bienfaiteurs si requis
      if (membershipData.includeEntryFee) {
        baseAmount += 500;
      }
    } else if (membershipData.membershipType === "donor") {
      // Pour les donateurs, le montant est seulement leur donation
      baseAmount = 0;
    }
    
    const totalAmount = baseAmount + membershipData.additionalDonation;
    
    // Génération d'un token de vérification
    const verificationToken = uuidv4();
    
    // Insertion dans la base de données
    const { data, error } = await supabase
      .from("memberships")
      .insert({
        first_name: membershipData.firstName,
        last_name: membershipData.lastName,
        email: membershipData.email,
        address: membershipData.address,
        city: membershipData.city,
        postal_code: membershipData.postalCode,
        membership_type: membershipData.membershipType,
        additional_donation: membershipData.additionalDonation,
        amount: totalAmount,
        payment_status: "pending_verification",
        verification_token: verificationToken
      })
      .select()
      .single();
    
    if (error) {
      console.error("Erreur lors de l'insertion de l'adhésion:", error);
      throw new Error(`Erreur lors de l'enregistrement de l'adhésion: ${error.message}`);
    }
    
    if (!data) {
      throw new Error("Aucune donnée retournée après l'insertion");
    }
    
    console.log("Adhésion enregistrée avec succès:", data);
    
    // Vérifier si l'email existe déjà dans la table charter_signatories
    let signatoryId = null;
    let signatureNumber = null;
    
    const { data: existingSignatory, error: searchError } = await supabase
      .from("charter_signatories")
      .select("id, signature_number")
      .eq("email", membershipData.email)
      .maybeSingle();
    
    if (searchError) {
      console.error("Erreur lors de la recherche du signataire existant:", searchError);
    }
    
    if (existingSignatory) {
      // L'utilisateur a déjà signé la charte, utiliser son ID et son numéro de signature existants
      console.log("Signataire existant trouvé:", existingSignatory);
      signatoryId = existingSignatory.id;
      signatureNumber = existingSignatory.signature_number;
      
      // Mettre à jour le signataire pour s'assurer que email_verified est à true pour les adhérents
      const { error: updateError } = await supabase
        .from("charter_signatories")
        .update({ email_verified: true })
        .eq("id", signatoryId);
      
      if (updateError) {
        console.error("Erreur lors de la mise à jour du signataire existant:", updateError);
      } else {
        console.log("Signataire existant mis à jour (email vérifié)");
      }
    } else {
      // L'utilisateur n'a pas encore signé la charte, l'ajouter à la table
      const { data: signatoryData, error: signatoryError } = await supabase
        .from("charter_signatories")
        .insert({
          first_name: membershipData.firstName,
          last_name: membershipData.lastName,
          email: membershipData.email,
          email_verified: true // Les adhérents sont considérés comme déjà vérifiés
        })
        .select()
        .single();
      
      if (signatoryError) {
        console.error("Erreur lors de l'ajout du signataire à la charte:", signatoryError);
      } else {
        console.log("Nouveau signataire de la charte ajouté avec succès:", signatoryData);
        signatoryId = signatoryData.id;
        signatureNumber = signatoryData.signature_number;
      }
    }
    
    // Si l'utilisateur souhaite s'abonner à la newsletter
    if (membershipData.acceptNewsletter) {
      console.log("L'utilisateur souhaite s'abonner à la newsletter");
      
      try {
        // Pour les adhésions (ACTIF, BIENFAITEUR ou DONATEUR), 
        // utiliser l'Edge Function mais avec le paramètre skipVerification à true
        const isMember = membershipData.membershipType === "active" || 
                         membershipData.membershipType === "benefactor" || 
                         membershipData.membershipType === "donor";
                
        console.log("Appel de la fonction add-newsletter-subscriber avec skipVerification:", isMember);
        
        // Appel unifié à la fonction Edge pour tous les types d'utilisateurs
        const { data: newsletterData, error: newsletterError } = await supabase.functions.invoke("add-newsletter-subscriber", {
          body: {
            email: membershipData.email,
            firstName: membershipData.firstName,
            lastName: membershipData.lastName,
            skipVerification: isMember // true pour les membres, false pour les autres
          }
        });
        
        if (newsletterError) {
          console.error("Erreur lors de l'ajout de l'abonné à la newsletter:", newsletterError);
        } else {
          console.log("Réponse de add-newsletter-subscriber:", newsletterData);
          if (isMember) {
            console.log("Abonné à la newsletter ajouté sans vérification (membre)");
          } else {
            console.log("Email de vérification newsletter envoyé");
          }
        }
      } catch (error) {
        // Ne pas bloquer le processus d'adhésion si l'ajout à la newsletter échoue
        console.error("Erreur lors de l'ajout à la newsletter:", error);
      }
    }
    
    // Envoi de l'email de vérification
    console.log("Envoi de l'email de vérification avec les données:", {
      firstName: membershipData.firstName,
      lastName: membershipData.lastName,
      email: membershipData.email,
      membershipId: data.id,
      verificationToken: verificationToken,
      membershipType: membershipData.membershipType,
      amount: totalAmount,
      signatoryId: signatoryId,
      signatureNumber: signatureNumber // Inclusion du numéro de signature
    });
    
    const emailResult = await supabase.functions.invoke("send-membership-verification", {
      body: {
        firstName: membershipData.firstName,
        lastName: membershipData.lastName,
        email: membershipData.email,
        membershipId: data.id,
        verificationToken: verificationToken,
        membershipType: membershipData.membershipType,
        amount: totalAmount,
        signatoryId: signatoryId,
        signatureNumber: signatureNumber // Inclusion du numéro de signature
      }
    });
    
    if (emailResult.error) {
      console.error("Erreur lors de l'envoi de l'email de vérification:", emailResult.error);
      throw new Error(`Erreur lors de l'envoi de l'email de vérification: ${emailResult.error.message}`);
    }
    
    console.log("Email de vérification envoyé avec succès:", emailResult.data);
    
    return {
      success: true,
      membershipId: data.id,
      verificationToken: verificationToken,
      signatoryId: signatoryId,
      signatureNumber: signatureNumber
    };
  } catch (error) {
    console.error("Erreur lors de la sauvegarde de l'adhésion:", error);
    throw error;
  }
}
